import { redirect, useLoaderData } from "react-router-dom";

import { get, pget, post } from "@/api";
import { Models } from "@/api/types";
import { CURRENT_ORDER_QUERY_KEY } from "@/modules/cart/useCartDetails";
import { GiftCardCheckoutContainer } from "@/modules/giftCards";
import { waitObj } from "@/toolbox/Promise";

import { queryClient } from "../../Root";

const getPlatform = pget("/platform/current");

async function loader() {
  let platform;
  try {
    platform = await queryClient.fetchQuery(getPlatform.key(), () =>
      getPlatform()
    );
  } catch (error) {
    console.error("Error fetching platform data:", error);
    throw redirect("/");
  }

  if (!platform?.platformSettings?.giftCardEnabled) {
    throw redirect("/");
  }

  const user = queryClient.fetchQuery(["/user"], () => get("/user"), {
    staleTime: 1000 * 60 * 2,
  });

  const cartDetails = await queryClient.ensureQueryData({
    queryKey: CURRENT_ORDER_QUERY_KEY,
    queryFn: () => get("/orders/current/details").catch(() => null),
  });

  let transactionDetails;

  if (cartDetails) {
    transactionDetails = await post(
      "/orders/{shopDisplayOrderId}/transactions",
      { shopDisplayOrderId: cartDetails.shopDisplayOrderId }
    );
  } else {
    transactionDetails = null;
  }

  const transfers = await get("/gift-cards/transfers/by-order/{orderId}", {
    orderId: cartDetails?.shopDisplayOrderId ?? "",
  });

  return waitObj({
    platform,
    user,
    cartDetails,
    transactionDetails,
    transfers,
  });
}

export const purchaseGiftCardCheckoutLoader = async () => {
  const data = await loader();
  if (!data.cartDetails || !data.transactionDetails) {
    return redirect("/cart");
  }

  if (
    data.transactionDetails.freePurchase &&
    !data.transactionDetails.setupIntentId
  ) {
    return redirect(
      `/gift-cards/success?shopDisplayOrderId=${data.cartDetails.shopDisplayOrderId}`
    );
  } else if (
    data.transactionDetails.transactionStatus === "Paid" ||
    data.transactionDetails.transactionStatus === "Processing"
  ) {
    return redirect(
      `/gift-cards/success?shopDisplayOrderId=${data.cartDetails.shopDisplayOrderId}&orderTransactionId=${data.transactionDetails.orderTransactionId}`
    );
  }

  return data;
};

const GiftCardCheckoutPage = () => {
  const { platform, cartDetails, transactionDetails, user, transfers } =
    useLoaderData() as {
      platform: Models<"PlatformModel">;
      cartDetails: Models<"ShopDisplayOrderResult"> | null;
      transactionDetails: Models<"InitiateOrderTransactionResult"> | null;
      user: Models<"UserModel1"> | null;
      transfers: Models<"DisplayGiftCardTransferItem">[];
    };

  return (
    <div className="GiftCardCheckoutPage">
      <GiftCardCheckoutContainer
        platform={platform}
        cartDetails={cartDetails}
        transactionDetails={transactionDetails}
        user={user}
        transfers={transfers}
      />
    </div>
  );
};

export default GiftCardCheckoutPage;
